/**
 * @fileoverview mountBuildUpdateBand — every web entry gives its page the update band, in a React root of the band's
 * own (WEB-APP-VERSION-REFRESH-001 U4d; coordinator ruling Q-U4D-PLACEMENT).
 * @package @puffin-core/auth-shell
 *
 * `<BuildUpdateNotice>` must reach EVERY page an open tab can show: signed-in pages, sign-in pages, the marketing
 * sites, the storefront and its checkout, the customer portals, the platform-admin screens, a crashed page. So it
 * mounts once per web entry, not once per layout.
 *
 * ## Why a root of its own, not a sibling in the app's tree
 *
 * Measured 2026-10-13. A sibling beside the app's `<AppErrorBoundary>` changes the app subtree's position in the React
 * tree. On a HYDRATED page (every prerendered marketing page) that:
 *
 *   - changed every `useId` — the server's `aria-controls="popover-_R_fd_"` met the client's `popover-_R_1tm_`, a
 *     hydration mismatch React does not patch;
 *   - and, before the notice passed a server snapshot to its store hooks, made React throw during hydration and
 *     discard the server-rendered page.
 *
 * The server renders the bare app in two places (the Vite prerender plugin and the API's blog renderer), so the trees
 * cannot be kept in step by adding the notice to one of them. A root of its own touches nothing in the app's tree. It
 * also keeps the band up when the app crashes, and a failure in the band cannot take the app down.
 *
 * ## Where it goes
 *
 * The band's container is inserted BEFORE the app's root element: the band is the first thing on the page, in document
 * flow, pushing the page down (never `position: fixed` over it). It publishes its height for the layouts that must
 * make room (`useBandHeightProperty`, shared-styles `--page-height`).
 *
 * Guard: `buildUpdateNoticeMounted` — every web entry calls this exactly once, and nothing else renders the notice.
 *
 * @module @puffin-core/auth-shell/mountBuildUpdateBand
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { BuildUpdateNotice } from './components/BuildUpdateNotice.jsx';

/** Marks the band's container, so a page can never hold two. */
export const BUILD_UPDATE_BAND_ROOT_ATTRIBUTE = 'data-build-update-band-root';

/**
 * @param {Object} args
 * @param {HTMLElement} args.beside - the app's root element (`#root`); the band's container goes before it
 * @param {Object} args.rootOptions - the React root's error options (`createReactRootErrorOptions(logger)` from
 *   `@puffin-core/logging-browser`), so a failure in the band is reported like any other root's
 * @returns {import('react-dom/client').Root} the band's root
 * @throws {TypeError} when `beside` is not an element, or `rootOptions` is missing
 * @throws {Error} when the page already has a band
 */
export function mountBuildUpdateBand(args) {
  // `args || {}`, not a `= {}` default: NULL args reach the named refusal below too, never a destructuring TypeError.
  const { beside, rootOptions } = args || {};
  if (!beside || typeof beside.before !== 'function') {
    throw new TypeError(
      'mountBuildUpdateBand: `beside` must be the app root element the band goes before.',
    );
  }
  if (!rootOptions || typeof rootOptions !== 'object') {
    throw new TypeError(
      'mountBuildUpdateBand: `rootOptions` is required — pass createReactRootErrorOptions(logger) so a failure in ' +
        'the band is reported.',
    );
  }
  const page = beside.ownerDocument;
  if (page.querySelector(`[${BUILD_UPDATE_BAND_ROOT_ATTRIBUTE}]`)) {
    throw new Error(
      'mountBuildUpdateBand: the update band is already mounted on this page — one per page.',
    );
  }
  const container = page.createElement('div');
  container.setAttribute(BUILD_UPDATE_BAND_ROOT_ATTRIBUTE, '');
  beside.before(container);
  const root = createRoot(container, rootOptions);
  root.render(
    <StrictMode>
      <BuildUpdateNotice />
    </StrictMode>,
  );
  return root;
}
